import { t } from "@lingui/macro";
import type { ResumeDto } from "@reactive-resume/dto";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";

import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { Button, Tooltip } from "@reactive-resume/ui";
import { ClockClockwise, MagnifyingGlassMinus, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { useViewStore } from "@/client/stores/view";
import { useLingui } from "@lingui/react";
import { useAnalysisContext } from "@/client/contexts/analysis-context";

export const BuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);
  const { i18n } = useLingui();
  // Use the context for all optimization and analysis states
  const {
    isLoadingOptimization,
    isOptimizing,
    isGeneratingQuestions,
    isSubscribed,
    optimizationStatus
  } = useAnalysisContext();

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);
  const leftPanelSize = useBuilderStore((state) => state.panel.left.size);
  const setActiveView = useViewStore((state) => state.setActiveView);

  const syncResumeToArtboard = useCallback(() => {
    setImmediate(() => {
      if (!frameRef?.contentWindow) return;
      console.log("syncResumeToArtboard", resume.data);
      const message = { type: "SET_RESUME", payload: resume.data };
      frameRef.contentWindow.postMessage(message, "*");
    });
  }, [frameRef?.contentWindow, resume.data]);

  // Send resume data to iframe on initial load
  useEffect(() => {
    if (!frameRef) return;

    frameRef.addEventListener("load", syncResumeToArtboard);

    return () => {
      frameRef.removeEventListener("load", syncResumeToArtboard);
    };
  }, [frameRef]);

  // Persistently check if iframe has loaded using setInterval
  useEffect(() => {
    const interval = setInterval(() => {
      if (frameRef?.contentWindow?.document.readyState === "complete") {
        syncResumeToArtboard();
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [frameRef]);

  // Send resume data to iframe on change of resume data
  useEffect(syncResumeToArtboard, [resume.data]);

  const onZoomIn = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");
  const onZoomOut = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");
  const onResetView = () => frameRef?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");

  const handleGoToAnalyze = () => {
    setActiveView("analytics");
  };

  const getRocketText = () => {
    if (isGeneratingQuestions) {
      return (
        <>
          {t`Loading...`} <br />
          {t`Generating Questions`}
        </>
      );
    }
    if (isOptimizing) {
      return (
        <>
          {t`Loading...`} <br />
          {t`Optimizing`}
        </>
      );
    }
    if (isLoadingOptimization) {
      return (
        <>
          {t`Loading...`} <br />
          {t`Optimization`}
        </>
      );
    }
    if (isSubscribed || optimizationStatus?.isInProgress) {
      return (
        <>
          {t`Loading...`} <br />
          {t`AI Optimizing`}
        </>
      );
    }
    return (
      <>
        {t`Boost chance`} <br />
        {t`with`} <strong>{t`AI`}</strong>!
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {title} - {t`CVKit`}
        </title>
        <html lang={i18n.locale} />
      </Helmet>

      <iframe
        ref={setFrameRef}
        title={resume.id}
        src="/artboard/builder"
        style={{
          height: `calc(100vh)`,
          width: `calc(100vw - ${leftPanelSize}%)`,
          left: `${leftPanelSize}%`,
          position: "fixed",
        }}
      />
      <div className="fixed right-2 bottom-2 z-10">
        <button
          onClick={handleGoToAnalyze}
          className="group relative flex transform cursor-pointer flex-col items-center justify-center overflow-hidden px-4 py-3 pt-20 font-semibold transition-all duration-300 hover:scale-[1.02] focus:ring-4 focus:ring-purple-500/50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img
            src="https://em-content.zobj.net/source/microsoft-teams/400/rocket_1f680.png"
            alt="Rocket"
            draggable="false"
            className="absolute top-[24px] z-9 size-18"
          />
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-50 via-gray-300/20 to-gray-100/20 px-4 py-3 pt-6 text-black backdrop-blur-sm dark:from-slate-900/70 dark:via-black/60 dark:to-slate-900/60 dark:text-white">
            <div className="animate-shimmer dark: absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-yellow-500/10 to-transparent dark:via-white/10"></div>
            {getRocketText()}
          </div>
        </button>
      </div>

      <div className="fixed top-[64px] right-0 z-10 flex flex-col gap-0.5 rounded-bl-md p-0.5 [&_svg]:drop-shadow-[0_0_1px_rgba(0,0,0,.3)] dark:[&_svg]:drop-shadow-[0_0_1px_rgba(0,0,0,.7)]">
        <Tooltip disableHoverableContent delay={100} side="left" content={t`Zoom In`}>
          <Button
            size="iconSm"
            variant="ghost"
            className="rounded-sm transition-all"
            onClick={onZoomIn}
          >
            <MagnifyingGlassPlus weight="duotone" />
          </Button>
        </Tooltip>
        <Tooltip disableHoverableContent delay={100} side="left" content={t`Zoom Out`}>
          <Button
            size="iconSm"
            variant="ghost"
            className="rounded-sm transition-all"
            onClick={onZoomOut}
          >
            <MagnifyingGlassMinus weight="duotone" />
          </Button>
        </Tooltip>
        <Tooltip disableHoverableContent delay={100} side="left" content={t`Reset Zoom`}>
          <Button
            size="iconSm"
            variant="ghost"
            className="rounded-sm transition-all"
            onClick={onResetView}
          >
            <ClockClockwise weight="duotone" />
          </Button>
        </Tooltip>
      </div>
    </>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    useResumeStore.setState({ resume });
    useResumeStore.temporal.getState().clear();

    return resume;
  } catch {
    return redirect("/dashboard");
  }
};
